'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string
  email: string
  phone: string
  postcode: string
  housingType: string
  message: string
}

export default function ContactFormSection() {
  const [isHydrated, setIsHydrated] = useState(false)
  const router = useRouter()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    housingType: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [consentChecked, setConsentChecked] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    // ── Step 1: Add contact to Brevo list 9 (dedicated endpoint, independent of email) ──
    try {
      const brevoRes = await fetch('/api/brevo-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          naam: formData.name,
          email: formData.email,
          telefoonnummer: formData.phone,
          postcode: formData.postcode,
          woningtype: formData.housingType,
          listId: 9,
        }),
      })
      if (!brevoRes.ok) {
        const brevoErr = await brevoRes.json().catch(() => ({}))
        console.error('[ContactForm] Brevo contact creation failed:', brevoErr)
      } else {
        console.log('[ContactForm] Brevo contact created successfully')
      }
    } catch (brevoErr) {
      console.error('[ContactForm] Brevo request exception:', brevoErr)
    }

    // ── Step 2: Send notification + confirmation emails ──
    try {
      const response = await fetch('/api/send-contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          naam: formData.name,
          email: formData.email,
          telefoonnummer: formData.phone,
          postcode: formData.postcode,
          woningtype: formData.housingType,
          bericht: formData.message,
          consent: consentChecked,
        }),
      })

      if (!response.ok) {
        let errorDetail = `HTTP ${response.status}`
        try {
          const errBody = await response.json()
          errorDetail = errBody?.error || errBody?.message || JSON.stringify(errBody)
        } catch {
          errorDetail = await response.text().catch(() => `HTTP ${response.status}`)
        }
        console.error('[ContactForm] Email API error:', errorDetail)
        throw new Error(errorDetail)
      }

      setIsSubmitting(false)
      // Redirect to confirmation page with lead data as query params
      const params = new URLSearchParams({
        naam: formData.name,
        email: formData.email,
        telefoon: formData.phone,
        postcode: formData.postcode,
        woningtype: formData.housingType,
      })
      router.push(`/bevestiging?${params.toString()}`)
    } catch (err) {
      console.error('[ContactForm] Submit failed:', err)
      setIsSubmitting(false)
      setSubmitStatus('error')
      setTimeout(() => setSubmitStatus('idle'), 5000)
    }
  }

  return (
    <section id="contact-form" className="py-20 md:py-32 relative overflow-hidden">
      {/* Bold gradient background to make section stand out */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0584F2] via-[#0A6FCC] to-[#0B5FAD]" />
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#33BEBE]/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/4 w-[300px] h-[300px] rounded-full bg-[#0584F2]/30 blur-2xl pointer-events-none" />

      <div className="mx-auto max-w-7xl px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column: Content */}
          <div className="space-y-8 reveal">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 border border-white/30 mb-6">
                <Icon name="ChatBubbleLeftRightIcon" size={16} className="text-white" />
                <span className="text-xs font-bold uppercase tracking-widest text-white">
                  Neem contact op
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
                Klaar voor kalkvrij water?
              </h2>
              <p className="text-lg text-white/85 leading-relaxed">
                Vraag een gratis, vrijblijvend adviesgesprek aan. Inclusief waterhardheidstest.
              </p>
            </div>

            {/* Trust Elements */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-[#C4E538]" />
                </div>
                <div>
                  <p className="font-bold text-white">Inclusief gratis waterhardheidstest</p>
                  <p className="text-sm text-white/75">
                    We meten de hardheid van jouw water en adviseren de beste oplossing.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-[#C4E538]" />
                </div>
                <div>
                  <p className="font-bold text-white">Vrijblijvend advies</p>
                  <p className="text-sm text-white/75">
                    Geen verplichtingen. We helpen je graag met persoonlijk advies.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-[#C4E538]" />
                </div>
                <div>
                  <p className="font-bold text-white">Installatieservice beschikbaar</p>
                  <p className="text-sm text-white/75">
                    Professionele installatie door gecertificeerde monteurs. In 1 uur klaar.
                  </p>
                </div>
              </div>
            </div>

            {/* Guarantee Badge */}
            <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 border border-white/25">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                  <Icon name="ShieldCheckIcon" size={32} className="text-white" />
                </div>
                <div>
                  <p className="text-lg font-heading font-bold text-white">
                    1 jaar geld-terug-garantie
                  </p>
                  <p className="text-sm text-white/75">
                    Niet tevreden? Volledige restitutie binnen 1 jaar.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="reveal">
            <div className="bg-white rounded-3xl p-8 md:p-10 shadow-2xl border border-white/50">
              {submitStatus === 'success' ? (
                <div className="text-center space-y-6 py-12">
                  <div className="h-20 w-20 rounded-full bg-[#C4E538]/15 flex items-center justify-center mx-auto">
                    <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-[#22c55e]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-[#0F1E2E] mb-2">
                      Bedankt voor je aanvraag!
                    </h3>
                    <p className="text-[#6B7280]">
                      We nemen binnen 24 uur contact met je op voor een gratis adviesgesprek.
                    </p>
                  </div>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="inline-flex items-center justify-center rounded-full border-2 border-[#0584F2] px-6 py-3 text-sm font-bold text-[#0584F2] hover:bg-[#0584F2] hover:text-white btn-transition"
                  >
                    Nog een aanvraag doen
                  </button>
                </div>
              ) : submitStatus === 'error' ? (
                <div className="text-center space-y-6 py-12">
                  <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                    <Icon name="ExclamationCircleIcon" size={48} variant="solid" className="text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-[#0F1E2E] mb-2">
                      Er is iets misgegaan
                    </h3>
                    <p className="text-[#6B7280]">
                      Je aanvraag kon niet worden verzonden. Probeer het opnieuw of neem direct contact met ons op.
                    </p>
                  </div>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="inline-flex items-center justify-center rounded-full border-2 border-[#0584F2] px-6 py-3 text-sm font-bold text-[#0584F2] hover:bg-[#0584F2] hover:text-white btn-transition"
                  >
                    Opnieuw proberen
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Early-bird discount urgency banner inside form */}
                  <div
                    className="rounded-2xl px-4 py-3 mb-2 flex items-center gap-3"
                    style={{
                      background: 'linear-gradient(135deg, #FFF7ED 0%, #FFF3E0 100%)',
                      border: '1.5px solid #FF6B0040',
                    }}
                  >
                    <div
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ background: '#FF6B00' }}
                    >
                      <Icon name="TagIcon" size={18} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black leading-tight" style={{ color: '#FF6B00' }}>
                        🎉 Tijdelijke vroegboekerskorting: −€250
                      </p>
                      <p className="text-xs leading-snug mt-0.5" style={{ color: '#92400E' }}>
                        Vul nu je gegevens in en claim je korting — beperkt beschikbaar!
                      </p>
                    </div>
                    <div
                      className="flex-shrink-0 rounded-full px-2 py-1 text-[10px] font-black text-white leading-none animate-pulse"
                      style={{ background: '#FF6B00' }}
                    >
                      OP = OP
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-2xl font-heading font-bold text-[#0F1E2E]">Gratis advies aanvragen</h3>
                    <p className="text-sm text-[#6B7280] mt-1">Vul het formulier in en we nemen snel contact op.</p>
                  </div>

                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-[#0F1E2E] mb-1.5">
                      Naam *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] bg-[#F9FAFB] text-[#0F1E2E] placeholder-[#9CA3AF] focus:border-[#0584F2] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0584F2]/10 transition-all"
                      placeholder="Je volledige naam"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-[#0F1E2E] mb-1.5">
                      E-mailadres *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] bg-[#F9FAFB] text-[#0F1E2E] placeholder-[#9CA3AF] focus:border-[#0584F2] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0584F2]/10 transition-all"
                      placeholder="je@email.nl"
                    />
                  </div>

                  {/* Phone + Postcode side by side */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-bold text-[#0F1E2E] mb-1.5">
                        Telefoon *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] bg-[#F9FAFB] text-[#0F1E2E] placeholder-[#9CA3AF] focus:border-[#0584F2] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0584F2]/10 transition-all"
                        placeholder="+31 6 12345678"
                      />
                    </div>
                    <div>
                      <label htmlFor="postcode" className="block text-sm font-bold text-[#0F1E2E] mb-1.5">
                        Postcode *
                      </label>
                      <input
                        type="text"
                        id="postcode"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] bg-[#F9FAFB] text-[#0F1E2E] placeholder-[#9CA3AF] focus:border-[#0584F2] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0584F2]/10 transition-all"
                        placeholder="1234 AB"
                      />
                    </div>
                  </div>

                  {/* Housing Type */}
                  <div>
                    <label htmlFor="housing-type-select" className="block text-sm font-bold text-[#0F1E2E] mb-1.5">
                      Woningtype *
                    </label>
                    <select
                      id="housing-type-select"
                      name="housingType"
                      value={formData.housingType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] bg-[#F9FAFB] text-[#0F1E2E] focus:border-[#0584F2] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0584F2]/10 transition-all"
                    >
                      <option value="">Selecteer woningtype</option>
                      <option value="Eengezinswoning">Eengezinswoning</option>
                      <option value="Appartement">Appartement</option>
                      <option value="Zakelijk">Zakelijk</option>
                      <option value="Advies nodig">Advies nodig</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-bold text-[#0F1E2E] mb-1.5">
                      Bericht (optioneel)
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] bg-[#F9FAFB] text-[#0F1E2E] placeholder-[#9CA3AF] focus:border-[#0584F2] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#0584F2]/10 transition-all resize-none"
                      placeholder="Vertel ons meer over je situatie..."
                    />
                  </div>

                  {/* Submit Button */}
<div className="flex items-start gap-3 mb-1">
  <div className="relative flex-shrink-0 mt-0.5">
    <input
      type="checkbox"
      id="consent"
      checked={consentChecked}
      onChange={(e) => setConsentChecked(e.target.checked)}
      required
      className="sr-only"
    />
    <div
      onClick={() => setConsentChecked(!consentChecked)}
      className={`w-5 h-5 rounded cursor-pointer border-2 flex items-center justify-center transition-all ${
        consentChecked
          ? 'bg-[#0584F2] border-[#0584F2]'
          : 'bg-white border-[#D1D5DB] hover:border-[#0584F2]'
      }`}
    >
      {consentChecked && (
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
  </div>
  <label
    htmlFor="consent"
    onClick={() => setConsentChecked(!consentChecked)}
    className="text-sm text-[#2F4F6E] leading-snug cursor-pointer select-none"
  >
    Ik geef FrenaCal toestemming om contact met mij op te nemen
  </label>
</div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !consentChecked}
                    className="w-full inline-flex items-center justify-center rounded-full bg-[#0584F2] px-8 py-4 text-base font-bold text-white hover:bg-[#0A6FCC] btn-transition shadow-lg shadow-[#0584F2]/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon name="ArrowPathIcon" size={20} className="mr-2 animate-spin" />
                        Verzenden...
                      </>
                    ) : (
                      <>
                        Gratis advies aanvragen
                        <Icon name="PaperAirplaneIcon" size={20} className="ml-2" />
                      </>
                    )}
                  </button>

                  {/* Privacy Notice */}
                  <p className="text-xs text-[#9CA3AF] text-center">
                    Door dit formulier te verzenden ga je akkoord met ons{' '}
                    <a href="#" className="text-[#0584F2] hover:underline">
                      privacybeleid
                    </a>
                    .
                  </p>

                  {/* Discount disclaimer */}
                  <p className="text-xs text-[#9CA3AF] text-center border-t border-[#F3F4F6] pt-3">
                    * De tijdelijke vroegboekerskorting van €250 is uitsluitend van toepassing op de{' '}
                    <span className="font-semibold text-[#6B7280]">FrenaCal FD Hogar</span> en{' '}
                    <span className="font-semibold text-[#6B7280]">FrenaCal FD Commercio</span> producten.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}